import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { userRequest } from '../utils/requestMethods';
import { useStateContext } from '../contexts/ContextProvider';
import { Header } from '../components';

const AddSchool = () => {
  const { user, refreshSchool, fetchUserSchools } = useStateContext();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    school_name: '',
    school_email: '',
    school_address: '',
    school_state: '',
    school_ownership_type: 'private',
    school_short_name: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Use the first school's ID for KYC reuse
      const firstSchoolId = user?.schools?.schools?.[0]?._id;
      
      if (!firstSchoolId) {
        throw new Error('No existing school found for KYC reuse');
      }

      const payload = {
        ...formData,
        reuse_kyc: true
      };

      await userRequest.post(`/schools/${firstSchoolId}/with-kyc-reuse`, payload);
      
      // Refresh both user schools and current school data
      await Promise.all([
        fetchUserSchools(),
        refreshSchool()
      ]);
      
      history.push('/');
    } catch (error) {
      console.error('Error creating school:', error);
      setError(error.response?.data?.message || error.message || 'Failed to create school');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
      <Header category="School Management" title="Add New School" />
      
      <div className="max-w-2xl mx-auto">
        <div className="bg-white shadow-lg rounded-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Create New School
          </h2>
          
          {error && (
            <div className="mb-4 p-4 text-sm text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  School Name *
                </label>
                <input
                  type="text"
                  name="school_name"
                  required
                  value={formData.school_name}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  placeholder="Enter school name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  School Email *
                </label>
                <input
                  type="email"
                  name="school_email"
                  required
                  value={formData.school_email}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  placeholder="Enter school email"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                School Address *
              </label>
              <input
                type="text"
                name="school_address"
                required
                value={formData.school_address}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="Enter school address"
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  School State *
                </label>
                <input
                  type="text"
                  name="school_state"
                  required
                  value={formData.school_state}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  placeholder="Enter school state"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  School Short Name
                </label>
                <input
                  type="text"
                  name="school_short_name"
                  value={formData.school_short_name}
                  onChange={handleInputChange}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  placeholder="Enter short name (optional)"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ownership Type
              </label>
              <select
                name="school_ownership_type"
                value={formData.school_ownership_type}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              >
                <option value="private">Private</option>
                <option value="public">Public</option>
                <option value="government">Government</option>
              </select>
            </div>
            
            <div className="pt-4">
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg disabled:opacity-50 transition-colors"
              >
                {loading ? 'Creating School...' : 'Create School'}
              </button>
            </div>
          </form>
          
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> This new school will reuse the KYC verification from your existing school, 
              so you won't need to go through the verification process again.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSchool; 